import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { notification } from 'antd';

import SoftwareImportContext from '@totem/components/devices/software-import/SoftwareImportContext';
import {
  SoftwareImportDeleteResult,
  SoftwareImportResult,
  SoftwareImportStatus,
  SoftwareImportSummary,
} from '@totem/components/devices/software-import/types';
import {
  readErrorMessage,
  validateCsvFile,
} from '@totem/components/devices/software-import/utilities';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { DEVICES_ENDPOINT } from '@totem/utilities/endpoints';

const SOFTWARE_IMPORT_ENDPOINT = `${DEVICES_ENDPOINT}/software/import`;

type Props = {
  children?: ReactNode;
};

const SoftwareImportContainer = ({ children }: Props) => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const [status, setStatus] = useState<SoftwareImportStatus>('idle');
  const [fileName, setFileName] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [result, setResult] = useState<SoftwareImportResult | null>(null);
  const [summary, setSummary] = useState<SoftwareImportSummary | null>(null);
  const [deleting, setDeleting] = useState<boolean>(false);

  const refreshSummary = useCallback(() => {
    fetch(SOFTWARE_IMPORT_ENDPOINT, {
      headers: { Authorization: `Bearer ${getApiToken()}` },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data: SoftwareImportSummary | null) => setSummary(data))
      .catch(() => setSummary(null));
  }, [getApiToken]);

  const onReset = () => {
    setStatus('idle');

    setFileName('');

    setError('');

    setResult(null);
  };

  // Results and the imported-device count belong to the active organization.
  useEffect(() => {
    onReset();

    setSummary(null);

    refreshSummary();
  }, [userProfile.organization.id, refreshSummary]);

  const onImport = async (file: File) => {
    setFileName(file.name);

    setError('');

    setResult(null);

    setStatus('validating');

    const validationError = await validateCsvFile(file);
    if (validationError) {
      setError(validationError);

      setStatus('failed');
      return;
    }

    setStatus('uploading');
    const form = new FormData();
    form.append('file', file);

    try {
      const response = await fetch(SOFTWARE_IMPORT_ENDPOINT, {
        method: 'POST',
        headers: new Headers({ Authorization: `Bearer ${getApiToken()}` }),
        body: form,
      });
      if (!response.ok) {
        const message = await readErrorMessage(response);
        setError(
          response.status === 403
            ? 'You must be an Organization Administrator to import software.'
            : message,
        );

        setStatus('failed');
        return;
      }
      setResult(await response.json());

      setStatus('complete');
    } catch (err) {
      setError(`The import could not be completed: ${(err as Error).message}`);

      setStatus('failed');
    } finally {
      refreshSummary();
    }
  };

  const onDeleteImported = async () => {
    setDeleting(true);

    try {
      const response = await fetch(SOFTWARE_IMPORT_ENDPOINT, {
        method: 'DELETE',
        headers: new Headers({ Authorization: `Bearer ${getApiToken()}` }),
      });
      if (!response.ok) {
        throw new Error(await readErrorMessage(response));
      }
      const deleted: SoftwareImportDeleteResult = await response.json();
      const total = deleted.devicesDeleted + deleted.devicesMarkedDeleted;
      notification.success({
        message: 'Imported devices deleted',
        description:
          deleted.devicesMarkedDeleted > 0
            ? `${total} device(s) deleted. ${deleted.devicesMarkedDeleted} referenced by tickets or events were marked deleted instead of removed.`
            : `${total} device(s) deleted.`,
      });

      onReset();
    } catch (err) {
      notification.error({
        message: 'Imported devices were not deleted',
        description: (err as Error).message,
        duration: 0,
      });
    } finally {
      setDeleting(false);

      refreshSummary();
    }
  };

  return (
    <SoftwareImportContext.Provider
      value={{
        status,
        fileName,
        error,
        result,
        summary,
        deleting,
        onImport,
        onReset,
        onDeleteImported,
      }}
    >
      {children}
    </SoftwareImportContext.Provider>
  );
};

export default SoftwareImportContainer;
